import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthProvider.jsx';

/**
 * /login page.
 *   - loading       → neutral spinner
 *   - authenticated → redirect back to the original destination (or /)
 *   - anonymous     → big "Sign in with Google" button.
 *
 * The button triggers a full-page navigation to /auth/google (handled by the
 * Express backend). React Router state is lost across that navigation, so we
 * pass the pre-login destination as `?next=/path` — the backend stashes it
 * in a short-lived cookie and honors it after the OAuth callback.
 */
export function LoginPage() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <main className="min-h-screen flex items-center justify-center text-t3">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading…
      </main>
    );
  }

  if (status === 'authenticated') {
    const from = location.state?.from || '/';
    return <Navigate to={from} replace />;
  }

  const authError = new URLSearchParams(location.search).get('auth_error');
  const errorInfo = describeAuthError(authError);
  const from = location.state?.from;
  const signInUrl = from ? `/auth/google?next=${encodeURIComponent(from)}` : '/auth/google';

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-s-2">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-bd p-8">
        <h1 className="text-2xl font-semibold text-t1">Webance Panel</h1>
        <p className="mt-1 text-sm text-t2">
          Sign in to manage WordPress sites and backups.
        </p>

        {authError && (
          <div className="mt-4 rounded-lg border border-err-bd bg-err-bg px-3 py-2 text-xs text-err">
            <div className="font-medium">{errorInfo.title}</div>
            <div className="mt-0.5 text-err">{errorInfo.detail}</div>
          </div>
        )}

        <a
          href={signInUrl}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-deep transition-colors"
        >
          <GoogleIcon className="w-4 h-4" />
          Sign in with Google
        </a>

        <p className="mt-6 text-xs text-t3">
          Access is restricted to approved email addresses.
        </p>
      </div>
    </main>
  );
}

/**
 * Map a backend `auth_error` code to a friendly {title, detail}. §8a adds the
 * DB-authorization outcomes (email_not_allowed / account_inactive) as clean,
 * non-technical messages instead of surfacing the raw code.
 */
function describeAuthError(code) {
  switch (code) {
    case 'email_not_allowed':
      return {
        title: 'Not authorized',
        detail:
          'This Google account isn’t authorized for the panel. Ask an admin to add it.',
      };
    case 'account_inactive':
      return {
        title: 'Account deactivated',
        detail: 'This account has been deactivated. Contact an admin to restore access.',
      };
    case 'email_not_verified':
      return {
        title: 'Email not verified',
        detail: 'Your Google email isn’t verified. Verify it with Google and try again.',
      };
    case 'state_mismatch':
      return {
        title: 'Sign-in expired',
        detail: 'The sign-in request expired or didn’t match. Please try again.',
      };
    default:
      return { title: 'Sign-in error', detail: `Something went wrong (${code}). Please try again.` };
  }
}

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#fff"
        d="M21.35 11.1H12v2.83h5.35c-.23 1.49-1.74 4.36-5.35 4.36-3.22 0-5.84-2.66-5.84-5.95s2.62-5.95 5.84-5.95c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.73 3.96 14.58 3 12 3 6.99 3 3 6.99 3 12s3.99 9 9 9c5.2 0 8.64-3.66 8.64-8.8 0-.59-.06-1.04-.13-1.1z"
      />
    </svg>
  );
}
